import './freeAccessActions.container.scss';
import { useTranslator } from '@Localization';
import { useInjection } from 'inversify-react';
import { observer } from 'mobx-react-lite';
import { ReactElement, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useCustomHotkeys } from '../../../../common/hooks/useCustomHotkeys';
import { ButtonWithLoader } from '../../../../design/buttons/ButtonWithLoader';
import { Loader } from '../../../components/Loader';
import { EnableFor } from '../../../containers/EnableForRole';
import { useLoader } from '../../../helpers/useLoader';
import { ACDLError } from '../../../models/error.model';
import { IAnnotationUiStore } from '../../annotation/annotationUi.store';
import { AnnotationStatusInfo } from '../../annotation/components/AnnotationStatusInfo';
import { IAnnotationTypeStore } from '../../annotation/submodules/annotationTypes/annotationType.store';
import { IClarificationsStore } from '../../annotation/submodules/clarifications/Clarifications.store';
import { ClarificationsContainer } from '../../annotation/submodules/clarifications/containers/Clarifications.container';
import { ClarifyButtonContainer } from '../../annotation/submodules/clarifications/containers/ClarifyButton.container';
import { ProjectStatus } from '../../projects/projects.model';
import { useFreeAccessPermissions } from '../freeAccess.permissions';
import { IFreeAccessService } from '../freeAccess.service';
import { AnnotateStage, FreeAccessMode, IFreeAccessStore } from '../freeAccess.store';
import { IFreeAccessApiService } from '../freeAccessApi.service';

export const FreeAccessActionsContainer = observer((): ReactElement => {
  const annotationTypeStore = useInjection(IAnnotationTypeStore.$);
  const freeAccessStore = useInjection(IFreeAccessStore.$);
  const freeAccessApiService = useInjection(IFreeAccessApiService.$);
  const freeAccessService = useInjection(IFreeAccessService.$);
  const uiData = useInjection(IAnnotationUiStore.$);
  const freeAccessPermissions = useFreeAccessPermissions();
  const clarificationStore = useInjection(IClarificationsStore.$);

  const { t } = useTranslator();
  const { isLoading, withLoaderAsync } = useLoader();
  const { projectId } = useParams<{ projectId: string }>();

  useEffect(() => {
    void freeAccessService.getProjectAnnotationInfoAsync(projectId);
  }, [projectId]);

  const changeModeAsync = async (mode: FreeAccessMode): Promise<void> => {
    if (isLoading) {
      return;
    }

    await withLoaderAsync(async () => {
      const { imageId, projectId } = freeAccessStore;
      const result = await freeAccessApiService.lockImageAsync(imageId, projectId, mode);

      if (result instanceof ACDLError) {
        await freeAccessService.updateLockImageInfoAsync();
      } else {
        await freeAccessService.changeModeAsync(mode);
      }
    }, 'free-access-action-button');
  };

  const onEditHotKeyPressed = async (): Promise<void> => {
    if (freeAccessStore.projectStatus === ProjectStatus.Draft && freeAccessStore.isImported) {
      return;
    }

    if (freeAccessStore.projectStatus !== ProjectStatus.Draft && freeAccessStore.annotateStage !== AnnotateStage.NOTANNOTATED) {
      await changeModeAsync(FreeAccessMode.ANNOTATIONRESET);
    }
  };

  const onAnnotateHotKeyPressed = async (): Promise<void> => {
    if (freeAccessStore.projectStatus === ProjectStatus.Draft && freeAccessStore.isImported) {
      return;
    }

    await changeModeAsync(freeAccessStore.annotateStage !== AnnotateStage.REJECTED ? FreeAccessMode.ANNOTATION : FreeAccessMode.CORRECT);
  };

  const onReviewHotKeyPressed = async (): Promise<void> => {
    if (freeAccessStore.projectStatus === ProjectStatus.Draft && freeAccessStore.isImported) {
      return;
    }

    if ([AnnotateStage.ANNOTATED, AnnotateStage.REVIEWED].includes(freeAccessStore.annotateStage)) {
      await changeModeAsync(FreeAccessMode.REVIEW);
    }
  };

  const {
    annotatedByName,
    reviewedByName,
    lockedByName,
    annotateStage,
    rejectionReason,
    isAssigned,
    freeAccessMode,
    status,
    isImported,
    isWaitingForReviewAfterReject,
    imageSet,
  } = freeAccessStore;
  const isImageLoading = uiData.isImageLoading;
  const isImportedAnnotationInDraft = freeAccessStore.projectStatus === ProjectStatus.Draft && isImported;
  const isImportedAnnotationInDraftTooltip = t('cant_do_anything_on_import_in_draft');

  let startButtonTooltip = '';
  let startButtonText = '';
  let mode: FreeAccessMode = FreeAccessMode.VIEW;

  if (annotateStage === AnnotateStage.NOTANNOTATED) {
    startButtonTooltip = t('start_annotation_tooltip');
    startButtonText = t('start_annotation');
    mode = FreeAccessMode.ANNOTATION;
  } else if (annotateStage === AnnotateStage.ANNOTATED) {
    startButtonTooltip = t('start_review_tooltip');
    startButtonText = t('start_review');
    mode = FreeAccessMode.REVIEW;
  } else if (annotateStage === AnnotateStage.DRAFT) {
    startButtonTooltip = t('continue_annotation_tooltip');
    startButtonText = t('continue_annotation');
    mode = FreeAccessMode.ANNOTATION;
  } else if (annotateStage === AnnotateStage.REJECTED) {
    startButtonTooltip = t('correct_annotation_tooltip');
    startButtonText = t('correct_annotation');
    mode = FreeAccessMode.CORRECT;
  } else {
    startButtonTooltip = t('review_again_tooltip');
    startButtonText = t('review_again');
    mode = FreeAccessMode.REVIEW;
  }

  const changeMode = async (): Promise<void> => {
    await changeModeAsync(mode);
  };
  const onEdit = async (): Promise<void> => {
    await changeModeAsync(FreeAccessMode.ANNOTATIONRESET);
  };
  const onReAnnotate = async (): Promise<void> => {
    await changeModeAsync(FreeAccessMode.ANNOTATION);
  };

  const hasUnansweredClarification = clarificationStore.hasUnansweredClarification;
  const hasNoMarkingToolOrQuestionSpecified = !freeAccessStore.questions.length && !annotationTypeStore.annotationTypes.length;

  const hotkeysEnabled =
    freeAccessMode === FreeAccessMode.VIEW && !hasNoMarkingToolOrQuestionSpecified && freeAccessPermissions.canUseKeyboardShortcuts().allowed;
  useCustomHotkeys(['e', 'E'], onEditHotKeyPressed, { enabled: hotkeysEnabled }, [onEditHotKeyPressed]);
  useCustomHotkeys(['a', 'A'], onAnnotateHotKeyPressed, { enabled: hotkeysEnabled }, [onAnnotateHotKeyPressed]);
  useCustomHotkeys(['r', 'R'], onReviewHotKeyPressed, { enabled: hotkeysEnabled }, [onReviewHotKeyPressed]);

  return (
    <Loader
      isLoading={isImageLoading}
      hideSpinner
    >
      <>
        {lockedByName && (
          <div className='free-access-actions-alert'>
            <span>{t('someone_is_working_on_image', { name: lockedByName })}</span>
          </div>
        )}
        <AnnotationStatusInfo
          isImageLoaded={true}
          annotationStatus={status}
          annotatedByName={annotatedByName}
          reviewedByName={reviewedByName}
          isAssigned={isAssigned}
          reviewRejectionReason={rejectionReason}
          hasUnansweredClarification={hasUnansweredClarification}
          isWaitingForReviewAfterReject={isWaitingForReviewAfterReject}
          imageSet={imageSet}
          enableImageSetChange={true}
          onImageSetChange={async (set) => {
            await freeAccessService.updateImageSet(set);
          }}
        />
        <ClarificationsContainer />
        {freeAccessMode === FreeAccessMode.VIEW && !hasNoMarkingToolOrQuestionSpecified && (
          <div className='free-access-actions'>
            {annotateStage !== AnnotateStage.NOTANNOTATED && (
              <EnableFor hasPermission={freeAccessPermissions.canEditAnnotation()}>
                <ButtonWithLoader
                  variant='outlined'
                  loaderKey='free-access-action-button'
                  id='edit-annotation-button'
                  color='primary'
                  disabled={!!lockedByName || isImportedAnnotationInDraft}
                  onClick={onEdit}
                  title={isImportedAnnotationInDraft ? isImportedAnnotationInDraftTooltip : t('edit_tooltip')}
                >
                  {t('edit')}
                </ButtonWithLoader>
              </EnableFor>
            )}
            {(annotateStage === AnnotateStage.ANNOTATED || annotateStage === AnnotateStage.REVIEWED) && !hasUnansweredClarification && (
              <EnableFor hasPermission={freeAccessPermissions.canReAnnotate()}>
                <ButtonWithLoader
                  variant='outlined'
                  loaderKey='free-access-action-button'
                  id='re-annotate-button'
                  color='primary'
                  disabled={!!lockedByName || isImportedAnnotationInDraft}
                  onClick={onReAnnotate}
                  title={isImportedAnnotationInDraft ? isImportedAnnotationInDraftTooltip : t('annotate_again_tooltip')}
                >
                  {t('annotate_again')}
                </ButtonWithLoader>
              </EnableFor>
            )}
            {!hasUnansweredClarification && (
              <EnableFor hasPermission={freeAccessPermissions.canChangeMode()}>
                <ButtonWithLoader
                  variant='outlined'
                  loaderKey='free-access-action-button'
                  id='change-mode-button'
                  color='primary'
                  onClick={changeMode}
                  title={isImportedAnnotationInDraft ? isImportedAnnotationInDraftTooltip : startButtonTooltip}
                >
                  {startButtonText}
                </ButtonWithLoader>
              </EnableFor>
            )}
            <ClarifyButtonContainer />
          </div>
        )}
      </>
    </Loader>
  );
});
